// Utility functions for OTTI application
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isValueNull } from '@1823-partners/core';

dayjs.extend(utc);

/**
 * Format date using dayjs for consistent date handling
 * @param {Date|string} date - Date to format
 * @param {string} format - Date format string (default: 'YYYY-MM-DD')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  if (!date) return '';
  // Use UTC to ensure consistent dates across all timezones
  return dayjs.utc(date).format(format);
};

/**
 * Determine whether a row is missing a meaningful comment.
 * A comment is considered missing when COMMENTS is null/undefined/empty/whitespace,
 * or equals 'N/A' (case-insensitive, trimmed). The backend default-fills 'N/A', so
 * 'N/A' MUST count as missing.
 *
 * @param {Object} row - OTTI data row
 * @returns {boolean} true when the comment is absent or N/A
 */
export const isMissingComment = (row) => {
  const value = row?.COMMENTS;
  // Use isValueNull from @1823-partners/core for the null/undefined/NaN cases.
  if (isValueNull(value)) return true;
  // Trim once and treat empty/whitespace-only or 'N/A' (case-insensitive) as
  // missing — the backend default-fills 'N/A'. The explicit empty check keeps
  // this correct regardless of how isValueNull handles whitespace.
  const trimmed = String(value).trim();
  return trimmed === '' || trimmed.toUpperCase() === 'N/A';
};

/**
 * Filter an array of OTTI rows to those that are missing a comment.
 *
 * @param {Array} data - Array of OTTI data objects
 * @returns {Array} Rows where isMissingComment is true
 */
export const needsCommentRows = (data) => {
  if (!data || data.length === 0) return [];
  return data.filter(isMissingComment);
};

// SPRD_FLG trips when CURR_SPRD has widened more than this many bps over
// ORIG_SPRD. Mirrors the backend SPRD_FLAG_THRESHOLD in otti_utils.py.
export const SPRD_FLAG_THRESHOLD_BPS = 400;

// Row fields whose edits change the derived flags. OttiGrid recomputes a row's
// SPRD_FLG / OTTI_FLG whenever one of these is edited in the grid, so the flags
// stay consistent after manual edits without a separate "Run Flags" step.
export const FLAG_INPUT_FIELDS = ['CURR_SPRD', 'ORIG_SPRD', 'G_DELTA', 'S_DELTA'];

/**
 * Compute the two derived OTTI flags for a single row from its spread and
 * NPV-delta inputs. Pure — returns { SPRD_FLG, OTTI_FLG } and does NOT mutate
 * the row. This is the single frontend source of truth for the flag math and
 * mirrors the backend (enrich_spreads' SPRD_FLG recompute + the beacon.otti
 * proc's OTTI_FLG), so a manual grid edit and a fresh Run OTTI agree.
 *
 * OTTI_FLG: a positive NPV delta means that basis has cushion, so the label
 * names the basis that is at risk (G_ = GAAP, S_ = STAT). It is only derived
 * when the row carries both G_DELTA and S_DELTA. Run OTTI (beacon.otti_build)
 * computes the deltas from NPV - BV and re-derives OTTI_FLG for every lot, so a
 * row without them (e.g. a lot uploaded before its first run) gets no OTTI_FLG
 * here: the caller keeps the row's value and the next run sets it, instead of
 * a "Possible OTTI" read off deltas the frontend does not have.
 *
 * @param {Object} row - OTTI data row
 * @returns {{SPRD_FLG: string, OTTI_FLG?: string}}
 */
export const computeRowFlags = (row) => {
  // A missing spread (null/undefined/blank/non-numeric) counts as 0 bps, as in
  // the backend: beacon.otti_build's coalesce(build, prior, 0) for both spreads
  // and enrich_otti_rows' _numeric(ORIG_SPRD, default=0). So CURR_SPRD 450
  // with no ORIG_SPRD flags here exactly as the next Run OTTI does.
  const currSprd = toFiniteNumberOrNull(row?.CURR_SPRD) ?? 0;
  const origSprd = toFiniteNumberOrNull(row?.ORIG_SPRD) ?? 0;
  const flags = { SPRD_FLG: currSprd - origSprd > SPRD_FLAG_THRESHOLD_BPS ? 'FLAG' : '' };

  const gDelta = toFiniteNumberOrNull(row?.G_DELTA);
  const sDelta = toFiniteNumberOrNull(row?.S_DELTA);
  if (gDelta === null || sDelta === null) return flags;

  if (gDelta > 0 && sDelta > 0) flags.OTTI_FLG = 'No OTTI';
  else if (gDelta > 0) flags.OTTI_FLG = 'Possible STAT OTTI';
  else if (sDelta > 0) flags.OTTI_FLG = 'Possible GAAP OTTI';
  else flags.OTTI_FLG = 'Possible OTTI';

  return flags;
};

/**
 * A grid cell's numeric value, or null when the cell is genuinely empty
 * (null/undefined/blank) or not a finite number. A measured 0 stays 0.
 *
 * @param {*} value - raw cell value (number or numeric string)
 * @returns {number|null}
 */
export const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/** `$1,234.56`; '' only for an empty or non-numeric cell (0 renders `$0.00`). */
export const formatCurrencyCell = (value) => {
  const n = toFiniteNumberOrNull(value);
  return n === null ? '' : `$${n.toLocaleString(undefined, TWO_DECIMALS)}`;
};

/** A fractional rate as a percent: 0.0525 → `5.2500%`; 0 renders `0.0000%`. */
export const formatRatePercentCell = (value) => {
  const n = toFiniteNumberOrNull(value);
  return n === null ? '' : `${(n * 100).toFixed(4)}%`;
};

/** A spread in bps: 250 → `250 bps`; 0 renders `0 bps`. */
export const formatBpsCell = (value) => {
  const n = toFiniteNumberOrNull(value);
  return n === null ? '' : `${n.toFixed(0)} bps`;
};

/** An NPV delta, negatives in parentheses: -15000 → `(15,000.00)`; 0 renders `0.00`. */
export const formatDeltaCell = (value) => {
  const n = toFiniteNumberOrNull(value);
  if (n === null) return '';
  const formatted = Math.abs(n).toLocaleString(undefined, TWO_DECIMALS);
  return n < 0 ? `(${formatted})` : formatted;
};

/** A whole-number count (DAYS); 0 renders `0`. */
export const formatIntegerCell = (value) => {
  const n = toFiniteNumberOrNull(value);
  return n === null ? '' : Math.trunc(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

/**
 * Parse an edited numeric cell. Clearing the cell yields null; text that is not
 * a finite number is rejected and the previous value is kept, so a bad paste
 * never silently blanks a saved number.
 *
 * @param {*} newValue - editor value
 * @param {*} oldValue - the cell's value before the edit
 * @returns {number|null|*}
 */
export const parseNumericEdit = (newValue, oldValue) => {
  if (newValue === null || newValue === undefined) return null;
  if (typeof newValue === 'string' && newValue.trim() === '') return null;
  const parsed = toFiniteNumberOrNull(newValue);
  if (parsed !== null) return parsed;
  return oldValue === undefined ? null : oldValue;
};

const NON_SP_OWNERS = new Set(['RA', 'DL']);

/**
 * Split OTTI rows into Structured Products vs Non SP.
 *
 * SP is a present SEC_OWNER that is not RA/DL. Blank, null, undefined, and
 * whitespace owners fold into Non SP so the two groups always partition the
 * payload (Total Records = SP + Non SP). Columbian lots land in Non SP until
 * Clearwater tags security_owner.
 *
 * @param {Array} data - Array of OTTI data objects
 * @returns {{sp: Array, nonSp: Array}}
 */
export const partitionOttiBySecOwner = (data) => {
  if (!data || data.length === 0) return { sp: [], nonSp: [] };
  const sp = [];
  const nonSp = [];
  for (const row of data) {
    const raw = row?.SEC_OWNER;
    const owner = typeof raw === 'string' ? raw.trim() : raw;
    if (owner && !NON_SP_OWNERS.has(owner)) {
      sp.push(row);
    } else {
      nonSp.push(row);
    }
  }
  return { sp, nonSp };
};